import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  Link,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuthStore } from '@/stores/auth.store';
import { authService, type LookupResponse, type OrganizationInfo } from '@/lib/auth.service';
import { getAPIErrorMessage } from '@/lib/api';
import { AvironIcon } from '@/components/ui/aviron-icon';
import { colors, typography as t, radius } from '@/theme/tokens';
import {
  armSignInHandoff,
  signInPanelProps,
  signInMarkProps,
  signInCopyProps,
  SIGN_IN_HEADLINE,
  SIGN_IN_SUBHEAD,
} from '@/stores/sign-in-handoff.store';

// The reset steps live in the same flow as sign-in: from 'password', the
// "Forgot password?" link emails a code and walks the user through reset in
// place, reusing the email + org already resolved.
// Order: email → method → password. 'method' lists the user's organizations
// (preselected when there is one) and the choice between the password form
// and Aviron Identity (Casdoor SSO).
type LoginStep = 'email' | 'method' | 'password' | 'reset-code' | 'reset-password' | 'reset-done';

// Which sign-in path is in flight — decides which button shows the spinner.
type LoginMethod = 'password' | 'sso';

// Move LoginContainer outside to prevent re-creation on every render
function LoginContainer({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%', bgcolor: 'background.paper' }}>
      {/* Left panel — dark branding. Tagged for the sign-in handoff: on a real
          sign-in this panel is measured and then reproduced by <SignInHandoff/>,
          which narrows it into the sidebar. See stores/sign-in-handoff.store. */}
      <Box
        {...signInPanelProps}
        sx={{
          width: '65%',
          minHeight: '100vh',
          bgcolor: colors.sidebar.bg,
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'flex-end',
          p: 7,
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 5 }}>
          <Box {...signInMarkProps} sx={{
            width: 40, height: 40, borderRadius: `${radius.lg}px`,
            bgcolor: colors.primary.main,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AvironIcon size={24} color={colors.sidebar.textActive} />
          </Box>
          <Typography sx={{ color: colors.sidebar.textActive, fontSize: 18, fontWeight: 700, fontFamily: t.fontFamily }}>
            Aviron MDM
          </Typography>
        </Box>

        {/* Headline. The strings live in the handoff store because the overlay
            has to redraw them after this page has already unmounted. */}
        <Typography {...signInCopyProps} sx={{
          color: colors.sidebar.textActive, fontSize: 40, fontWeight: 700, lineHeight: 1.15,
          letterSpacing: '-0.01em',
          fontFamily: t.fontFamily, mb: 2, maxWidth: 520,
        }}>
          {SIGN_IN_HEADLINE}
        </Typography>
        <Typography sx={{
          color: colors.sidebar.text, fontSize: t.fontSize.md, lineHeight: 1.55,
          fontFamily: t.fontFamily, maxWidth: 440,
        }}>
          {SIGN_IN_SUBHEAD}
        </Typography>
      </Box>

      {/* Right panel — form */}
      <Box
        sx={{
          width: { xs: '100%', md: '35%' },
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 5,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 400 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAuth, isAuthenticated } = useAuthStore();

  const [step, setStep] = useState<LoginStep>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingType, setLoadingType] = useState<LoginMethod | null>(null);
  const submittingRef = useRef(false);
  const [error, setError] = useState('');

  // Form fields
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<OrganizationInfo | null>(null);

  // Inline forgot-password reset fields
  const [resetCode, setResetCode] = useState('');
  const [resetToken, setResetToken] = useState(''); // binds the verified Casdoor session
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleCallback = useCallback(async (code: string, state?: string) => {
    setIsLoading(true);
    setError('');

    try {
      const tokens = await authService.handleCallback(code, state);
      // Measure while this page is still the thing on screen.
      armSignInHandoff();
      setAuth(tokens);
      window.history.replaceState({}, '', '/login');
      navigate('/');
    } catch (err) {
      console.error('Auth callback error:', err);
      setError('Authentication failed. Please try again.');
      window.history.replaceState({}, '', '/login');
    } finally {
      setIsLoading(false);
    }
  }, [setAuth, navigate]);

  // Handle OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');

    if (code) {
      handleCallback(code, state || undefined);
    }
  }, [searchParams, handleCallback]);

  // Pre-fill email from URL query parameter (only on mount)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam) {
      setEmail(emailParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Redirect if already authenticated. Deliberately does NOT arm the sign-in
  // handoff: this fires when someone lands on /login with a session already in
  // localStorage, which is a bounce rather than a sign-in. Animating it would
  // replay the transition on every refresh — the same mistake as keying off
  // isAuthenticated, which auth.store persists.
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const validateEmail = (value: string): boolean => {
    if (!value.trim()) {
      setEmailError('Email address is required');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email');
      return false;
    }
    setEmailError('');
    return true;
  };

  // Asks the backend for the Casdoor authorize URL (scoped to the org, email
  // as login_hint) and navigates there. Casdoor comes back to
  // /login?code=...&state=..., which the callback effect above exchanges.
  // On success the page is unloading, so the spinner is left on.
  const redirectToSSO = async (org: OrganizationInfo) => {
    setIsLoading(true);
    setLoadingType('sso');
    setError('');

    try {
      await authService.login(org.name, email.trim());
    } catch (err) {
      console.error('SSO redirect error:', err);
      setError('Failed to redirect to login. Please try again.');
      setIsLoading(false);
      setLoadingType(null);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateEmail(email)) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setIsLoading(true);
    setError('');

    try {
      const result: LookupResponse = await authService.lookupUser(email.trim());

      if (!result.user_exists || !result.organizations?.length) {
        setError('No account found with this email');
        return;
      }

      setOrganizations(result.organizations);
      setSelectedOrg(result.organizations[0]);
      setStep('method');
    } catch (err) {
      console.error('Lookup error:', err);
      setError('Failed to lookup user. Please try again.');
    } finally {
      setIsLoading(false);
      submittingRef.current = false;
    }
  };

  // Method step: password form, or hand off to Aviron Identity.
  const handleChoosePassword = () => {
    setError('');
    setStep('password');
  };

  const handleSSOLogin = () => {
    if (!selectedOrg) return;
    void redirectToSSO(selectedOrg);
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!password.trim()) {
      setPasswordError('Password is required');
      return;
    }
    setPasswordError('');

    if (!selectedOrg) return;
    if (submittingRef.current) return;
    submittingRef.current = true;

    setIsLoading(true);
    setLoadingType('password');
    setError('');

    try {
      const tokens = await authService.loginWithPassword(email, password, selectedOrg.name);
      // Measure while this page is still the thing on screen.
      armSignInHandoff();
      setAuth(tokens);
      navigate('/');
    } catch (err: unknown) {
      console.error('Password login error:', err);
      const axiosErr = err as { response?: { status?: number; data?: { message?: string } } };
      if (axiosErr?.response?.status === 429) {
        setPasswordError('Account locked: too many failed attempts. Try again in a few minutes or contact your administrator.');
      } else {
        setPasswordError('Invalid password. Please try again.');
      }
      setIsLoading(false);
      setLoadingType(null);
      submittingRef.current = false;
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setSelectedOrg(null);
    setOrganizations([]);
    setError('');
    setPassword('');
    setPasswordError('');
  };

  const handleBackToMethod = () => {
    setStep('method');
    setError('');
    setPassword('');
    setPasswordError('');
  };

  // ── Inline forgot-password reset (routed through the MDM backend) ──────
  // The backend drives Casdoor server-to-server, so the browser never calls
  // Casdoor. From the password step this emails a code; the code step then
  // advances locally and the final submit verifies the code + sets the new
  // password in ONE backend call (Casdoor's verify + set-password must share
  // a single session, which the backend handles).
  const resetErrMessage = (err: unknown): string => {
    return getAPIErrorMessage(err, 'Something went wrong. Please try again.');
  };

  const handleForgotPassword = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);
    setError('');
    setPasswordError('');
    try {
      await authService.forgotSend(email.trim());
      setResetCode('');
      setResetToken('');
      setStep('reset-code');
    } catch (err) {
      setError(resetErrMessage(err));
    } finally {
      setIsLoading(false);
      submittingRef.current = false;
    }
  };

  // Verify the code against the backend and, on success, keep the reset token
  // that binds the verified Casdoor session for the set-password call. Errors
  // (wrong / expired code) surface here at the code step.
  const handleVerifyResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (resetCode.trim().length < 4) { setError('Enter the code from your email.'); return; }
    setIsLoading(true);
    setError('');
    try {
      const res = await authService.forgotVerify(email.trim(), resetCode.trim());
      setResetToken(res.reset_token);
      setStep('reset-password');
    } catch (err) {
      setError(resetErrMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendResetCode = async () => {
    setError('');
    setResetToken('');
    try { await authService.forgotSend(email.trim()); } catch { /* generic — no reveal */ }
  };

  const handleSubmitNewPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Use at least 6 characters.'); return; }
    if (newPassword !== confirmPassword) { setError('Passwords do not match.'); return; }
    setIsLoading(true);
    setError('');
    try {
      await authService.forgotReset(email.trim(), resetCode.trim(), resetToken, newPassword);
      setNewPassword('');
      setConfirmPassword('');
      setStep('reset-done');
    } catch (err) {
      // Usually a password-policy rejection — stay on this step so the user can
      // adjust the password (the code is already verified).
      setError(resetErrMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  // Return from a reset step to sign-in with the same email/org.
  const handleBackToPassword = () => {
    setStep('password');
    setError('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setPassword('');
    setPasswordError('');
  };

  // Show loading state during callback processing
  if (searchParams.get('code')) {
    return (
      <LoginContainer>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography color="text.secondary">Completing sign in...</Typography>
        </Box>
      </LoginContainer>
    );
  }

  // Organization selection step
  // Method chooser — org is known; password form or Aviron Identity redirect.
  if (step === 'method') {
    return (
      <LoginContainer>
        <Typography sx={{ mb: 4, fontSize: 22, fontWeight: t.fontWeight.bold, color: colors.text.primary, fontFamily: t.fontFamily }}>
          How do you want to sign in?
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" component="span">
            {email}
          </Typography>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToEmail}
            sx={{ ml: 1, color: colors.primary.main }}
          >
            Change
          </Link>
        </Box>

        <RadioGroup
          value={selectedOrg?.name || ''}
          onChange={(e) => {
            const org = organizations.find((o) => o.name === e.target.value);
            if (org) setSelectedOrg(org);
          }}
        >
          {organizations.map((org) => (
            <Card
              key={org.name}
              variant="outlined"
              sx={{
                mb: 1,
                borderColor: selectedOrg?.name === org.name ? colors.primary.main : 'divider',
                bgcolor: selectedOrg?.name === org.name ? alpha(colors.primary.main, 0.07) : 'transparent',
                transition: 'border-color 0.15s ease-out, background-color 0.15s ease-out',
                '&:hover': {
                  borderColor: colors.primary.main,
                },
                '&:focus-within': {
                  borderColor: colors.primary.main,
                  outline: `2px solid ${alpha(colors.primary.main, 0.3)}`,
                  outlineOffset: 1,
                },
              }}
            >
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <FormControlLabel
                  value={org.name}
                  control={<Radio sx={{ color: colors.primary.main, '&.Mui-checked': { color: colors.primary.main } }} />}
                  label={
                    <Box>
                      <Typography variant="body1" fontWeight={500}>
                        {org.display_name || org.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Sign in as {org.username}
                      </Typography>
                    </Box>
                  }
                  sx={{ m: 0, width: '100%', cursor: 'pointer' }}
                />
              </CardContent>
            </Card>
          ))}
        </RadioGroup>

        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleChoosePassword}
          disabled={!selectedOrg || isLoading}
          sx={{ mt: 3, height: 48, bgcolor: colors.primary.main, borderRadius: `${radius.md}px`, fontFamily: t.fontFamily, '&:hover': { bgcolor: colors.primary.hover } }}
        >
          CONTINUE WITH PASSWORD
        </Button>

        <Divider sx={{ my: 3 }}>
          <Typography variant="caption" color="text.secondary">
            or
          </Typography>
        </Divider>

        <Button
          variant="outlined"
          fullWidth
          size="large"
          onClick={handleSSOLogin}
          disabled={!selectedOrg || isLoading}
          sx={{
            height: 48,
            borderColor: colors.border.light,
            color: colors.text.primary,
            borderRadius: `${radius.md}px`,
            fontFamily: t.fontFamily,
            '&:hover': { borderColor: colors.primary.main, color: colors.primary.main },
          }}
        >
          {loadingType === 'sso' ? <CircularProgress size={24} /> : 'Sign in with Aviron Identity'}
        </Button>
      </LoginContainer>
    );
  }

  if (step === 'password') {
    return (
      <LoginContainer>
        <Typography sx={{ mb: 4, fontSize: 22, fontWeight: t.fontWeight.bold, color: colors.text.primary, fontFamily: t.fontFamily }}>
          Enter Password
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {email}
          </Typography>
          {selectedOrg && (
            <Typography variant="caption" color="text.secondary">
              {selectedOrg.display_name || selectedOrg.name} • {selectedOrg.username}
            </Typography>
          )}
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToMethod}
            sx={{ ml: 1, color: colors.primary.main }}
          >
            Back
          </Link>
        </Box>

        <Box component="form" onSubmit={handlePasswordSubmit}>
          {/* Hidden username field: this multi-step login collects the email
              on a previous step, so the password form has no username for the
              browser to associate — which suppresses the "Save password?"
              prompt and autofill. A present-but-offscreen username field
              (not display:none, which password managers ignore) fixes both. */}
          <input
            type="email"
            name="username"
            autoComplete="username"
            value={email}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
            style={{ position: 'absolute', width: 1, height: 1, padding: 0, border: 0, opacity: 0, pointerEvents: 'none' }}
          />
          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            autoComplete="current-password"
            autoFocus
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={isLoading}
            sx={{ height: 48, bgcolor: colors.primary.main, borderRadius: `${radius.md}px`, fontFamily: t.fontFamily, '&:hover': { bgcolor: colors.primary.hover } }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'SIGN IN'}
          </Button>
        </Box>

        <Box sx={{ mt: 2.5, textAlign: 'center' }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={handleForgotPassword}
            disabled={isLoading}
            sx={{ color: colors.primary.main, fontWeight: t.fontWeight.medium }}
          >
            Forgot password?
          </Link>
        </Box>
      </LoginContainer>
    );
  }

  // Reset step 1 — enter the emailed code
  if (step === 'reset-code') {
    return (
      <LoginContainer>
        <Typography sx={{ mb: 0.5, fontSize: 22, fontWeight: t.fontWeight.bold, color: colors.text.primary, fontFamily: t.fontFamily }}>
          Reset your password
        </Typography>
        <Typography sx={{ mb: 4, fontSize: t.fontSize.base, color: colors.text.secondary, fontFamily: t.fontFamily }}>
          We sent a code to <strong>{email}</strong>. It expires shortly.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleVerifyResetCode}>
          <TextField
            label="Verification code"
            fullWidth
            autoFocus
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputProps={{ inputMode: 'numeric', maxLength: 6, style: { letterSpacing: '0.4em', fontFamily: 'monospace' } }}
            placeholder="123456"
            sx={{ mb: 3 }}
          />
          <Button
            type="submit" variant="contained" fullWidth size="large" disabled={isLoading}
            sx={{ height: 48, bgcolor: colors.primary.main, borderRadius: `${radius.md}px`, fontFamily: t.fontFamily, '&:hover': { bgcolor: colors.primary.hover } }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'VERIFY CODE'}
          </Button>
        </Box>

        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component="button" type="button" variant="body2" onClick={handleResendResetCode} sx={{ color: colors.primary.main }}>
            Resend code
          </Link>
        </Box>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Link component="button" type="button" variant="body2" onClick={handleBackToPassword} sx={{ color: colors.text.secondary }}>
            ← Back to sign in
          </Link>
        </Box>
      </LoginContainer>
    );
  }

  // Reset step 2 — choose a new password
  if (step === 'reset-password') {
    return (
      <LoginContainer>
        <Typography sx={{ mb: 0.5, fontSize: 22, fontWeight: t.fontWeight.bold, color: colors.text.primary, fontFamily: t.fontFamily }}>
          Set a new password
        </Typography>
        <Typography sx={{ mb: 4, fontSize: t.fontSize.base, color: colors.text.secondary, fontFamily: t.fontFamily }}>
          Choose a strong password you don't use elsewhere.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmitNewPassword}>
          <TextField
            label="New password"
            type={showNewPassword ? 'text' : 'password'}
            fullWidth
            autoFocus
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm password"
            type={showNewPassword ? 'text' : 'password'}
            fullWidth
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={{ mb: 3 }}
          />
          <Button
            type="submit" variant="contained" fullWidth size="large" disabled={isLoading}
            sx={{ height: 48, bgcolor: colors.primary.main, borderRadius: `${radius.md}px`, fontFamily: t.fontFamily, '&:hover': { bgcolor: colors.primary.hover } }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'UPDATE PASSWORD'}
          </Button>
        </Box>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Link component="button" type="button" variant="body2" onClick={handleBackToPassword} sx={{ color: colors.text.secondary }}>
            ← Back to sign in
          </Link>
        </Box>
      </LoginContainer>
    );
  }

  // Reset step 3 — done
  if (step === 'reset-done') {
    return (
      <LoginContainer>
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 56, color: colors.success.main, mb: 2 }} />
          <Typography sx={{ mb: 1, fontSize: 22, fontWeight: t.fontWeight.bold, color: colors.text.primary, fontFamily: t.fontFamily }}>
            Password updated
          </Typography>
          <Typography sx={{ mb: 3, fontSize: t.fontSize.base, color: colors.text.secondary, fontFamily: t.fontFamily }}>
            You can now sign in with your new password.
          </Typography>
          <Button
            variant="contained" fullWidth size="large" onClick={handleBackToPassword}
            sx={{ height: 48, bgcolor: colors.primary.main, borderRadius: `${radius.md}px`, fontFamily: t.fontFamily, '&:hover': { bgcolor: colors.primary.hover } }}
          >
            BACK TO SIGN IN
          </Button>
        </Box>
      </LoginContainer>
    );
  }

  // Email step (default)
  return (
    <LoginContainer>
      <Typography sx={{ mb: 0.5, fontSize: 22, fontWeight: t.fontWeight.bold, color: colors.text.primary, fontFamily: t.fontFamily }}>
        Sign in to your account
      </Typography>
      <Typography sx={{ mb: 4, fontSize: t.fontSize.base, color: colors.text.secondary, fontFamily: t.fontFamily }}>
        Enter your email to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleEmailSubmit}>
        <TextField
          label="Email address"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
          placeholder="admin@example.com"
          autoComplete="email"
          sx={{ mb: 3 }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={isLoading}
          sx={{ height: 48, bgcolor: colors.primary.main, borderRadius: `${radius.md}px`, fontFamily: t.fontFamily, '&:hover': { bgcolor: colors.primary.hover } }}
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : 'CONTINUE'}
        </Button>
      </Box>
    </LoginContainer>
  );
}
